import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { X, XCircle } from 'lucide-react'

import Button from '@/components/ui/Button'
import Label from '@/components/ui/Label'
import { apiErrorMessage } from '@/lib/api'
import { cn } from '@/lib/cn'

import {
  useRejectVacationRequest,
  type AdminVacationRequest,
} from '@/features/admin/use-admin-vacation'

const TYPE_LABEL: Record<AdminVacationRequest['type'], string> = {
  WYPOCZYNKOWY: 'Wypoczynkowy',
  NA_ZADANIE: 'Na żądanie',
  BEZPLATNY: 'Bezpłatny',
  CHOROBOWY: 'Chorobowy (L4)',
  OPIEKA_NAD_DZIECKIEM: 'Opieka nad dzieckiem',
}

const rejectSchema = z.object({
  decisionNote: z
    .string()
    .min(1, 'Uzasadnienie jest wymagane')
    .max(500, 'Maksymalnie 500 znaków'),
})
type RejectValues = z.infer<typeof rejectSchema>

interface DialogProps {
  request: AdminVacationRequest
  open: boolean
  onClose: () => void
}

function formatRange(start: string, end: string): string {
  if (start === end) return start
  return `${start} – ${end}`
}

/**
 * Dialog TYLKO dla odmowy. Zatwierdzenie idzie jednym kliknięciem z listy —
 * komentarz przy akceptacji był zbędnym krokiem (i tak zwykle pusty).
 * Uzasadnienie przy odmowie zostaje: pracownik musi wiedzieć dlaczego.
 */
export default function AdminVacationRejectDialog({
  request,
  open,
  onClose,
}: DialogProps) {
  const reject = useRejectVacationRequest()
  const form = useForm<RejectValues>({
    resolver: zodResolver(rejectSchema),
    defaultValues: { decisionNote: '' },
  })

  useEffect(() => {
    form.reset({ decisionNote: '' })
  }, [request.id, form])

  if (!open) return null

  async function onSubmit(values: RejectValues) {
    try {
      await reject.mutateAsync({
        id: request.id,
        decisionNote: values.decisionNote.trim(),
      })
      toast.success('Wniosek odrzucony')
      onClose()
    } catch (err) {
      toast.error(apiErrorMessage(err))
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-bg-canvas/80 backdrop-blur-sm p-4"
    >
      <div className="rounded-card border border-border-subtle bg-bg-elevated p-6 max-w-md w-full">
        <header className="flex items-start justify-between gap-4 mb-2">
          <div className="flex items-start gap-3">
            <XCircle className="h-5 w-5 text-danger" aria-hidden />
            <h2 className="text-lg font-semibold">Odrzucić wniosek?</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center h-8 w-8 rounded-action text-text-muted hover:text-text-primary hover:bg-bg-surface transition"
            aria-label="Zamknij"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </header>
        <p className="text-sm text-text-muted">
          <span className="font-medium text-text-primary">{request.user.name}</span>
          {request.user.position && (
            <span className="text-text-faint"> ({request.user.position.name})</span>
          )}
          {' — '}
          {TYPE_LABEL[request.type]}{' '}
          <span className="text-text-faint">
            {formatRange(request.startDate, request.endDate)} · {request.workingDays} dni
          </span>
        </p>
        {request.comment && (
          <p className="mt-2 text-xs text-text-muted italic">
            Komentarz pracownika: „{request.comment}"
          </p>
        )}

        <form onSubmit={form.handleSubmit(onSubmit)} noValidate className="space-y-4 mt-4">
          <div>
            <Label htmlFor="reject-note">Uzasadnienie odrzucenia</Label>
            <textarea
              id="reject-note"
              rows={3}
              maxLength={500}
              placeholder="Krótkie uzasadnienie dla pracownika."
              className={cn(
                'w-full rounded-action border bg-bg-elevated px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-accent/40',
                form.formState.errors.decisionNote
                  ? 'border-danger/60'
                  : 'border-border-strong',
              )}
              {...form.register('decisionNote')}
            />
            {form.formState.errors.decisionNote && (
              <p className="mt-1 text-xs text-danger">
                {form.formState.errors.decisionNote.message}
              </p>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-border-subtle">
            <Button type="button" variant="secondary" onClick={onClose}>
              Anuluj
            </Button>
            <Button
              type="submit"
              loading={reject.isPending}
              className="bg-danger text-bg-elevated hover:opacity-90"
            >
              Odrzuć
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
